"use client";

import { cn } from "@/lib/utils";
import { useCheckoutStore } from "@/store/useCheckoutStore";
import { CHECKOUT_STEPS } from "@/components/screen/start-order/components/Checkout/constants";
import { Typography } from "@/components/ui/typography";
import { memo, useEffect, useState } from "react";
import ProgressBar from "../ProgressBar";

const CheckoutProgressSteps = ({ className }: React.ComponentProps<"div">) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const currentStep = useCheckoutStore((store) => store.currentStep);
  const maxReachedStep = useCheckoutStore((store) => store.maxReachedStep);
  const goToStep = useCheckoutStore((store) => store.goToStep);
  const validateCurrentStep = useCheckoutStore((store) => store.validateCurrentStep);

  const handleStepClick = async (step: number) => {
    if (step > maxReachedStep) {
      return;
    }

    if (step < currentStep) {
      goToStep(step);
      return;
    }

    const isValid = await validateCurrentStep();
    if (isValid) {
      goToStep(step);
    }
  };

  return (
    <div
      className={cn(
        "flex justify-center items-start pt-3.5 h-12.5 flex-auto min-w-0",
        className
      )}
    >
      <div
        className="text-[#58666c] bg-neutral-200 flex justify-between h-1 relative flex-auto min-w-0"
        style={{ maxWidth: 108 * CHECKOUT_STEPS.length }}
      >
        {isMounted && (
          <ProgressBar className="absolute h-full left-0 inset-y-0 bg-blue-500 rounded-r-[60px] w-0 will-change-[width] transition-[width] duration-300" />
        )}
        {CHECKOUT_STEPS.map(({ id, label }, index) => {
          const step = index + 1;
          const isClickable = step <= maxReachedStep;
          const isCompleted = currentStep > step;
          const isCurrent = currentStep === step;

          const StepWrapper = isClickable ? "button" : "span";

          return (
            <span
              key={id}
              className="w-0 h-full flex justify-center relative z-[5] -mt-1.5 first:-mr-3.5 last:-ml-3.5"
            >
              <StepWrapper
                {...(isClickable && {
                  onClick: () => handleStepClick(step),
                  "aria-label": `Go to ${label} step`,
                  "aria-current": isCurrent ? "step" : undefined,
                  type: "button",
                })}
                style={{
                  WebkitTapHighlightColor: "transparent",
                  WebkitTouchCallout: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                }}
                className={cn(
                  "grid grid-cols-1 gap-1 place-content-start justify-items-center text-center bg-transparent border-0 p-0 [&::before]:!hidden [&::after]:!hidden touch-manipulation select-none outline-none",
                  {
                    "cursor-pointer hover:opacity-80":
                      isClickable,
                  }
                )}
              >
                <span
                  className={cn(
                    "transition-colors w-4 h-4 rounded-full shrink-0 bg-neutral-200 border-3 border-transparent",
                    {
                      "border-blue-500 bg-white": isMounted && isCurrent,
                      "border-blue-500 bg-blue-500": isMounted && isCompleted,
                    }
                  )}
                />
                <span className="relative flex justify-center">
                  <Typography
                    variant="labelSm"
                    className={cn(
                      "transition-colors whitespace-nowrap absolute right-full translate-x-1/2",
                      {
                        "text-blue-500": isMounted && currentStep >= step,
                      }
                    )}
                    asChild
                  >
                    <span>{label}</span>
                  </Typography>
                </span>
              </StepWrapper>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default memo(CheckoutProgressSteps);
